import { listActiveWorkflowsForEvent, executeWorkflowRun } from '@/lib/coreWorkflow';
import { emitCoreNotification, type NotificationChannel } from '@/lib/coreNotification';
import { resolveSettings } from '@/lib/coreSettings';
import { filterOutboundByConsent } from '@/lib/coreConsent';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — Consommateurs d'événements (spec 39 → 40)
// Le fait d'émettre un événement dans l'Event Bus ne suffit pas : il faut
// le « consommer ». Ce module branche un événement sur ses consommateurs :
//
//   emitEvent(order.created)
//        │
//        ├── Workflow Engine : exécute les workflows actifs dont le
//        │   déclencheur correspond (notifications, rappels, demandes d'avis…)
//        └── Notifications : émet une notification du socle (in-app / email)
//
// Tout est best-effort : un consommateur en échec ne bloque jamais le métier
// ni les autres consommateurs.
// ─────────────────────────────────────────────────────────────

interface EventNotificationDef {
  type: string;
  channel: NotificationChannel;
  title: string;
  body?: string;
}

/**
 * Correspondance événement du socle → notification par défaut.
 * Les modules peuvent la compléter ; c'est le « câblage » par défaut.
 */
export const EVENT_NOTIFICATION_MAP: Record<string, EventNotificationDef> = {
  'customer.created': { type: 'system', channel: 'in_app', title: 'Nouveau client ajouté', body: 'Une nouvelle fiche client vient d’être créée.' },
  'order.created': { type: 'system', channel: 'in_app', title: 'Nouvelle commande', body: 'Une nouvelle commande a été enregistrée.' },
  'order.completed': { type: 'order_shipped', channel: 'in_app', title: 'Commande terminée', body: 'Une commande vient d’être finalisée.' },
  'invoice.created': { type: 'system', channel: 'in_app', title: 'Nouvelle facture émise', body: 'Une facture vient d’être créée.' },
  'invoice.paid': { type: 'payment_received', channel: 'in_app', title: 'Facture payée', body: 'Un paiement de facture vient d’être enregistré.' },
  'payment.received': { type: 'payment_received', channel: 'in_app', title: 'Paiement reçu', body: 'Un paiement vient d’être reçu.' },
  'booking.created': { type: 'booking_confirmed', channel: 'in_app', title: 'Nouvelle réservation', body: 'Une nouvelle réservation a été enregistrée.' },
  'booking.confirmed': { type: 'booking_confirmed', channel: 'in_app', title: 'Réservation confirmée', body: 'Une réservation vient d’être confirmée.' },
  'appointment.created': { type: 'appointment_reminder', channel: 'in_app', title: 'Nouveau rendez-vous', body: 'Un nouveau rendez-vous a été pris.' },
  'appointment.confirmed': { type: 'appointment_reminder', channel: 'in_app', title: 'Rendez-vous confirmé', body: 'Un rendez-vous vient d’être confirmé.' },
  'appointment.cancelled': { type: 'system', channel: 'in_app', title: 'Rendez-vous annulé', body: 'Un rendez-vous a été annulé.' },
};

/** Finalité de consentement qui gouverne les canaux sortants d'un événement. */
export const EVENT_CONSENT_PURPOSE: Record<string, string> = {
  'customer.created': 'notifications',
  'order.created': 'communications',
  'order.completed': 'communications',
  'invoice.created': 'notifications',
  'invoice.paid': 'notifications',
  'payment.received': 'notifications',
  'booking.created': 'notifications',
  'booking.confirmed': 'notifications',
  'appointment.created': 'notifications',
  'appointment.confirmed': 'notifications',
  'appointment.cancelled': 'notifications',
};

export interface ConsumeCoreEventInput {
  tenantId: number | null;
  organizationId: number;
  eventKey: string;
  entityType?: string | null;
  entityId?: string | number | null;
  payload?: Record<string, unknown> | null;
  /** Destinataire dont le consentement est vérifié pour les canaux sortants. */
  recipientUserId?: number | null;
}

export interface ConsumeCoreEventResult {
  workflowsRun: number;
  notificationSent: boolean;
  /** Nombre de canaux sortants bloqués faute de consentement. */
  blockedChannels: number;
}

/**
 * Consomme un événement du socle : exécute les workflows correspondants,
 * puis émet la notification par défaut. Ne lève jamais.
 */
export async function consumeCoreEvent(input: ConsumeCoreEventInput): Promise<ConsumeCoreEventResult> {
  const result: ConsumeCoreEventResult = { workflowsRun: 0, notificationSent: false, blockedChannels: 0 };

  // 1. Workflow Engine — exécuter les workflows actifs dont le déclencheur correspond.
  try {
    const workflows = await listActiveWorkflowsForEvent(input.organizationId, input.eventKey);
    for (const wf of workflows) {
      try {
        await executeWorkflowRun({
          workflowId: wf.id,
          organizationId: input.organizationId,
          tenantId: input.tenantId,
          triggerEvent: input.eventKey,
          entityType: input.entityType ?? null,
          entityId: input.entityId ?? null,
          payload: input.payload ?? null,
          mode: 'live',
        });
        result.workflowsRun += 1;
      } catch {
        /* un workflow en échec ne doit pas bloquer les suivants */
      }
    }
  } catch {
    /* best-effort */
  }

  // 2. Notifications — la cascade de paramètres (spec 50) décide des canaux
  //    réellement activés (email, push, SMS). In-app est toujours actif.
  const def = EVENT_NOTIFICATION_MAP[input.eventKey];
  if (def) {
    let nsettings: Record<string, unknown> = {};
    try {
      nsettings = await resolveSettings({ organizationId: input.organizationId, tenantId: input.tenantId });
    } catch {
      nsettings = {};
    }
    const requested: NotificationChannel[] = ['in_app'];
    if (nsettings.notifications_email !== false) requested.push('email');
    if (nsettings.notifications_push === true) requested.push('push');
    if (nsettings.notifications_sms === true) requested.push('sms');

    // Consentements (spec 56/57) : on retire les canaux sortants non consentis.
    const purpose = EVENT_CONSENT_PURPOSE[input.eventKey] || 'notifications';
    let channels: NotificationChannel[] = requested;
    try {
      const decision = await filterOutboundByConsent({
        userId: input.recipientUserId ?? null,
        purpose,
        channels: requested,
      });
      channels = decision.allowed as NotificationChannel[];
      result.blockedChannels = decision.blocked.length;
    } catch {
      channels = requested;
    }

    for (const channel of channels) {
      try {
        await emitCoreNotification({
          tenantId: input.tenantId,
          organizationId: input.organizationId,
          channel,
          type: def.type,
          title: def.title,
          body: def.body || null,
          entityType: input.entityType ?? null,
          entityId: input.entityId != null ? String(input.entityId) : null,
          status: channel === 'in_app' ? undefined : 'pending',
          metadata: { event_key: input.eventKey, channel, payload: input.payload ?? null },
        });
        result.notificationSent = true;
      } catch {
        /* best-effort */
      }
    }
  }

  return result;
}